<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, useTemplateRef, nextTick } from 'vue'
import FormButton from './FormButton.vue'
import { createPopper } from '@popperjs/core'
import { useRoute } from 'vue-router'

const isVisible = ref(false)
const emit = defineEmits(['toggle', 'visibility-change'])

const route = useRoute()

// Props
const props = defineProps({
  type: {
    type: String,
    default: 'main',
  },
  size: {
    type: String,
    default: 'md',
  },
  isIcon: {
    type: Boolean,
    default: false,
  },
  fullWidth: {
    type: Boolean,
    default: false,
  },
  noBorder: {
    type: Boolean,
    default: false,
  },
  ariaLabel: {
    type: String,
    default: undefined,
  },
  submenu: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
  },
  position: {
    type: String,
    default: 'left'
  },
  zIndexVisible: {
    type: String,
    default: 'z-11'
  },
  listClasses: {
    type: String,
    default: ''
  },
  listWidth: {
    type: String,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  customClasses: {
    type: String,
  },
  wrapperClass: {
    type: String,
  },
  maxHeight: {
    type: String,
  },
  to: {
    type: String,
  },
  customDropdownClass: {
    type: String,
  },
  onClick: Function,
  preventHide: {
    type: Boolean,
    default: false
  },
  useTeleport: {
    type: Boolean,
    default: false,
  },
  noPopper: {
    type: Boolean,
    default: false,
  },
  /** When the menu opens, focus the first text field inside it (e.g. a filter input). */
  autofocusMenuInput: {
    type: Boolean,
    default: false,
  },
})

// Template Ref
const btnDropdownRef = useTemplateRef('btnDropdownRef')
const DropdownRef = useTemplateRef('DropdownRef')

// Refs
const position = ref({
  x: 0,
  y: 0,
  width: 0,
  height: 0
})

const root = ref(null)
let popperInstance = null

const getButtonEl = () => {
  const btn = btnDropdownRef.value
  return btn?.$el || btn || null
}

const destroyPopper = () => {
  popperInstance?.destroy()
  popperInstance = null
}

const initPopper = async () => {
  // CSS-positioned (non-teleport) menus keep their existing layout. Popper is
  // only used when the panel is portaled to <body>, so it can flip above the
  // trigger instead of being clipped by overflow:hidden ancestors.
  if (props.noPopper || !props.useTeleport) return
  await nextTick()
  const reference = getButtonEl()
  const popperEl = DropdownRef.value
  if (!reference || !popperEl) return
  destroyPopper()
  popperInstance = createPopper(reference, popperEl, {
    placement: popperPlacement.value,
    strategy: 'fixed',
    modifiers: [
      { name: 'offset', options: { offset: [0, 4] } },
      {
        name: 'flip',
        options: { fallbackPlacements: ['top-end', 'top-start', 'bottom-start', 'bottom-end'] },
      },
      { name: 'preventOverflow', options: { padding: 8 } },
    ],
  })
}

// Computed
const maxHeightClass = computed(() => props.maxHeight ? `max-h-${props.maxHeight} overflow-y-auto` : 'max-h-[300px] overflow-y-auto')

const popperPlacement = computed(() => {
  switch (props.position) {
    case 'right':
    case 'mobile-right':
      return 'bottom-end'
    case 'left':
    case 'mobile-left':
      return 'bottom-start'
    case 'top-right':
      return 'top-end'
    case 'top-left':
      return 'top-start'
    case 'auto':
      return 'auto'
    case 'auto-end':
      return 'auto-end'
    case 'auto-start':
      return 'auto-start'
    default:
      return 'bottom-start'
  }
})

const classes = computed(() => props.disabled ? 'flex items-center justify-center h-8 w-8 text-surface-8 disabled:text-surface-5 leading-none' : 'flex justify-center items-center h-8 w-8 text-surface-8 hover:text-white leading-none')

const rootComputedClass = computed(() => isVisible.value ? props.zIndexVisible : '')

// FormButton ignores its own disabled: styles when customClasses is passed, so
// the disabled look has to be baked into the class string we build here.
const disabledClass = computed(() => props.disabled ? ' opacity-50 cursor-not-allowed pointer-events-none !text-surface-6' : '')

const buttonComputed = computed(() => {
  if (props.isIcon) return classes.value
  if (props.type === 'flat') return 'w-full min-w-0 overflow-hidden bg-surface-3 rounded-md flex justify-between items-center h-8 text-tiny !px-4' + disabledClass.value
  if (props.type === 'secondary') return 'bg-surface-3 rounded-md flex justify-between items-center h-8 text-tiny !px-4 text-nowrap' + disabledClass.value
  if (props.type === 'outline') return 'text-surface-text-1 hover:text-white bg-transparent !border border-surface-4 rounded-md flex justify-between items-center h-8 text-tiny !px-4 text-nowrap hover:border-surface-6' + disabledClass.value
  return props.customClasses
})

const positionClass = computed(() => {
  if (props.submenu) {
    switch (props.position) {
      case 'left': return 'left-0 w-full top-0'
      case 'mobile-right': return 'right-0 md:left-0'
      case 'mobile-left': return 'left-0 md:right-0'
      case 'right': return 'right-0'
    }
  } else {
    switch (props.position) {
      case 'left': return 'left-0'
      case 'top-left': return 'left-0 bottom-8'
      case 'mobile-right': return 'right-0 md:left-0 md:right-auto'
      case 'mobile-left': return 'left-0 md:right-0'
      case 'right': return 'right-0'
      case 'top-right': return 'right-0 bottom-8'
    }
  }
})

// Hooks
onMounted(() => {
  window.addEventListener('click', forceClose)
  window.addEventListener('resize', handleResize)
  // Get initial position
  getPosition()
})

onBeforeUnmount(() => {
  window.removeEventListener('click', forceClose)
  window.removeEventListener('resize', handleResize)
  destroyPopper()
})

// Watch
watch(() => route.params, async () => {
  if (!props.preventHide) isVisible.value = false
})

watch(isVisible, (newVal) => {
  emit('visibility-change', newVal)
  if (newVal) {
    getPosition()
  } else {
    destroyPopper()
  }
})

// Methods
const toggleDropdown = () => {
  if (!props.disabled) isVisible.value = !isVisible.value
  emit('toggle', isVisible.value)
  if (isVisible.value) {
    getPosition()
    initPopper()
    focusMenuInput()
  } else {
    destroyPopper()
  }
}

const handleResize = _.debounce(() => {
  getPosition()
  if (isVisible.value) initPopper()
})

const forceClose = (event) => {
  const target = event.target
  // The clicked node was removed from the DOM while the click was handled
  // (e.g. a calendar day / "Set" button inside an inline picker that closes
  // itself on select). Its detached state would fail the contains() check
  // below and wrongly close this dropdown, so ignore it.
  if (target && target.isConnected === false) return
  if (root.value?.contains(target)) return
  // Teleported menus live on <body>, so they are not inside `root`.
  if (DropdownRef.value?.contains?.(target)) return
  // VueDatePicker menus teleported to <body> should not dismiss this dropdown
  if (target?.closest?.('.dp__menu, .dp__outer_menu_wrap, .dp--menu-wrapper')) return
  isVisible.value = false
  destroyPopper()
}

const hideDropDownMenu = () => {
  isVisible.value = false
  destroyPopper()
}

// Compatibility with previous dropdown API usage
const show = () => {
  isVisible.value = true
  getPosition()
  initPopper()
  focusMenuInput()
}

const hide = () => {
  isVisible.value = false
  destroyPopper()
}

const getPosition = () => {
  if (btnDropdownRef.value) {
    const rect = btnDropdownRef.value.$el.getBoundingClientRect()
    position.value = {
      x: rect.left, // X-coordinate relative to the viewport
      y: rect.top,  // Y-coordinate relative to the viewport
      width: rect.width,
      height: rect.height
    }
  }
}

const focusMenuInput = async () => {
  if (!props.autofocusMenuInput) return
  await nextTick()
  const el = DropdownRef.value?.querySelector?.('input:not([type=hidden]):not(.hidden), textarea')
  el?.focus()
}

defineExpose({ toggleDropdown, hideDropDownMenu, show, hide })

</script>

<template>
  <div ref="root" class="dropdown-wrapper relative min-w-0" :class="[rootComputedClass, { 'w-full': props.fullWidth }]">
    <FormButton :fullWidth="props.fullWidth" :isIcon="isIcon" ref="btnDropdownRef" :disabled="disabled"
      :type="props.type" :size="size" :aria-label="props.ariaLabel" @click="toggleDropdown" :customClasses="buttonComputed">
      {{ label }}
      <slot name="toggle-button" />
    </FormButton>
    <template v-if="props.useTeleport">
      <Teleport to='body'>
        <div ref="DropdownRef"
          :class="[isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none', wrapperClass, listWidth, customDropdownClass, { 'w-full': props.fullWidth }]"
          class="fixed bg-surface-2 rounded-md z-40 shadow-dropdown transition-opacity duration-150 ease-out">
          <ul :class="['py-1', maxHeightClass, listClasses, props.fullWidth ? 'w-full' : '']">
            <slot name="menu-items" />
          </ul>
        </div>
      </Teleport>
    </template>
    <template v-else>
      <div ref="DropdownRef"
        :class="[isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-1 pointer-events-none', wrapperClass, listWidth, positionClass, customDropdownClass, { 'w-full': props.fullWidth }]"
        class="absolute bg-surface-2 rounded-md z-30 shadow-dropdown origin-top transition-[opacity,transform] duration-150 ease-out">
        <ul :class="['py-1', maxHeightClass, listClasses, props.fullWidth ? 'w-full' : '']">
          <slot name="menu-items" />
        </ul>
      </div>
    </template>
  </div>
</template>

<style></style>
